import React, { useCallback, useMemo } from 'react';

import type { ChatViewerHandle } from 'chat-viewer';

import {
  ButtonSize,
  IconButton,
  IconButtonThemes,
  CounterBadge,
} from '@wix/wix-ui-tpa/cssVars';
import { ChevronDown } from '@wix/wix-ui-icons-common/on-stage';
import { useTranslation } from '@wix/yoshi-flow-editor';

import type { Message } from '@/features/messages/model';

import { st, classes } from './ScrollToNewMessagesButton.st.css';

type ScrollToNewMessagesButtonProps = {
  visible: boolean;
  count: number;
  chatRef: React.RefObject<ChatViewerHandle<Message> | null>;
  onScrollToBottom: (chat: ChatViewerHandle<Message>) => void;
};

function formatAriaWithCount(template: string, count: number): string {
  return template
    .replace(/\{\{count\}\}/g, String(count))
    .replace(/\{count\}/g, String(count));
}

export const ScrollToNewMessagesButton: React.FC<
  ScrollToNewMessagesButtonProps
> = ({ visible, count, chatRef, onScrollToBottom }) => {
  const { t } = useTranslation();

  const onClick = useCallback(() => {
    const chat = chatRef.current;
    if (chat) {
      onScrollToBottom(chat);
    }
  }, [chatRef, onScrollToBottom]);

  const ariaLabel = useMemo(
    () =>
      formatAriaWithCount(t('app.widget.chat.scrollToNewMessagesAria'), count),
    [t, count],
  );

  if (!visible) {
    return null;
  }

  return (
    <div className={st(classes.root)}>
      <IconButton
        theme={IconButtonThemes.Box}
        icon={<ChevronDown />}
        onClick={onClick}
        className={st(classes.button)}
        aria-label={ariaLabel}
        size={ButtonSize.medium}
      />
      {count > 0 && (
        <span className={st(classes.countBadge)}>
          <CounterBadge maximum={99} value={count} />
        </span>
      )}
    </div>
  );
};
